import { createSelector } from 'reselect'
import {
  get,
  getIn,
  getEq,
  safeToJS,
  pickToJS,
  where,
  getSatisfies,
} from 'utils/immutable'
import {
  pick,
  pathOr,
  lte,
  gte,
  __,
  both,
  ifElse,
  test,
  either,
  isEmpty,
  isNil,
} from 'ramda'
import {
  CHECPLUS_MODULE_TYPES,
  XMC_MODULES,
  NEW,
  RETURNED,
  VEHICLE_SUBTYPES,
} from 'utils/constants'
import { INTERNAL } from 'constants/featureTypes'

import {
  isAlphanumeric,
  required,
  isMaxLength,
  isLength,
  truthy,
} from 'utils/validation'

import { getFeaturesRouteMatch } from 'selectors/router'
import { featureMetaDataSelector } from './shared'

const isProperMask = test(/^[a-z0-9]{3}-[a-z0-9]{3}$/i) // ***-***
const lte80Chars = isMaxLength(80)
const equals9AlphanumericChars = both(isAlphanumeric, isLength(9))
const from0To100 = both(gte(__, 0), lte(__, 100))
const emptyOrMasked = either(isEmpty, isProperMask)

const validateInternal = where({
  description: both(lte80Chars, required),
  releaseNumber: both(equals9AlphanumericChars, required),
  subGroup: both(truthy, required),
  priority: both(from0To100, required),
  successorCode: emptyOrMasked,
})

const validateExternal = getSatisfies(both(lte80Chars, required), 'description')

const validate = ifElse(
  getEq('type', INTERNAL),
  validateInternal,
  validateExternal
)

export const getFeatureIsValid = createSelector(
  featureMetaDataSelector,
  validate
)

export const getFeatureMetadata = createSelector(
  featureMetaDataSelector,
  (feature) =>
    pick(
      [
        'id',
        'description',
        'masterNumber',
        'releaseNumber',
        'status',
        'moduleName',
        'moduleType',
        'orgId',
        'partNumber',
        'type',
        'subGroup',
        'successorCode',
        'priority',
        'minCompatibleVariant',
      ],
      safeToJS(feature, {})
    )
)

const featureEditorMetaDataSelector = createSelector(
  getIn(['feature', 'currentFeature', 'feature']),
  getFeaturesRouteMatch,
  (value, match) => {
    const featureId = pathOr(
      value.keySeq().first(),
      ['params', 'featureId'],
      match
    )
    return get(featureId, value)
  }
)

export const getFeatureEditorMetadata = createSelector(
  featureEditorMetaDataSelector,
  (feature) =>
    pickToJS(
      [
        'id',
        'description',
        'masterNumber',
        'releaseNumber',
        'status',
        'moduleName',
        'moduleType',
        'orgId',
        'partNumber',
        'type',
        'subGroup',
        'successorCode',
        'priority',
      ],
      feature
    )
)

export const getFeatureType = createSelector(
  featureMetaDataSelector,
  get('type')
)

export const getFeatureModuleName = createSelector(
  featureMetaDataSelector,
  get('moduleName')
)

export const getFeatureVehicleSubType = createSelector(
  featureMetaDataSelector,
  get('vehicleSubType')
)

export const getIsSchoolBusFeatureSubType = createSelector(
  getFeatureVehicleSubType,
  (featureVehicleSubtype) => {
    if (isNil(featureVehicleSubtype)) return false
    return VEHICLE_SUBTYPES.BUS === featureVehicleSubtype
  }
)

const { XMC, ASAM } = CHECPLUS_MODULE_TYPES

export const getFeatureModuleType = createSelector(
  getFeatureModuleName,
  (moduleName) => (XMC_MODULES.includes(moduleName) ? XMC : ASAM)
)

export const getIsASAMFeature = createSelector(
  getFeatureModuleName,
  (moduleName) => moduleName === ASAM
)

export const getCurrentFeatureId = createSelector(
  featureMetaDataSelector,
  (featureMetadata) => featureMetadata.get('id')
)

const getFeatureStatus = createSelector(featureMetaDataSelector, get('status'))

export const getInDesignStatus = createSelector(
  getFeatureStatus,
  (status) => status === NEW || status === RETURNED
)

export const getCurrentEditingFeatureModuleName = createSelector(
  getFeaturesRouteMatch
)

const featureSelector = (state) => state.get('feature')

export const getFeaturePreviousStatus = createSelector(
  featureSelector,
  get('previousStatus')
)
